import { IndiaAuditWorkspace } from "./auditflow/IndiaAuditWorkspace";
import { useState, useMemo } from "react";
import {
  ENGAGEMENT_TYPE_LABELS, STAGE_LABELS, STAGE_ORDER, CARO_2020_CLAUSES,
  FORM_3CD_KEY_CLAUSES, OPINION_LABELS, FINDING_SEVERITY_LABELS,
  engagementCompletionPercent, stageReadiness, remainingStages,
  type AuditEngagement, type EngagementType, type EngagementStage,
  type Workpaper, type AuditFinding, type PbcRequest, type CaroClause, type RiskArea
} from "./auditflow";
import { processBridgeEvent, clientContextFor } from "./auditflow/bridge";
import type { Client } from "./clientdesk/types";

const today = () => new Date().toISOString().slice(0, 10);
const uid = (p: string) => `${p}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

// -------------------------------------------------------- Shared

function StageRail({ stage, completion }: { stage: EngagementStage; completion: number }) {
  const activeIdx = STAGE_ORDER.indexOf(stage);
  return (
    <div className="af-stage-rail">
      {STAGE_ORDER.map((s, i) => (
        <div key={s} className={`af-stage-node ${i < activeIdx ? "done" : i === activeIdx ? "active" : "upcoming"}`}>
          <div className="af-stage-dot">{i < activeIdx ? "✓" : i + 1}</div>
          <span>{STAGE_LABELS[s]}</span>
        </div>
      ))}
      <div className="af-stage-track">
        <div className="af-stage-progress" style={{ width: `${completion}%` }} />
      </div>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: AuditFinding["severity"] }) {
  const color = { critical: "red", significant: "red", moderate: "orange", minor: "slate", informational: "blue" }[severity];
  return <span className={`badge badge-${color}`}>{FINDING_SEVERITY_LABELS[severity]}</span>;
}

// --------------------------------------------------- Engagement creation form

interface NewEngagementFormProps {
  clients: Client[];
  existingClientId?: string;
  onCreate: (engagement: AuditEngagement) => void;
  onCancel: () => void;
  notify: (msg: string, tone?: string) => void;
}

export function NewEngagementForm({ clients, existingClientId, onCreate, onCancel, notify }: NewEngagementFormProps) {
  const [clientId, setClientId] = useState(existingClientId ?? "");
  const [type, setType] = useState<EngagementType>("statutory-audit");
  const [financialYear, setFinancialYear] = useState("2025-26");
  const [periodEnd, setPeriodEnd] = useState("2026-03-31");
  const [partner, setPartner] = useState("");
  const [manager, setManager] = useState("");
  const [teamInput, setTeamInput] = useState("");

  const selectedClient = clients.find(c => c.id === clientId);
  const clientCtx = selectedClient ? clientContextFor(selectedClient) : null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || !partner) return;

    const engagement: AuditEngagement = {
      id: uid("eng"),
      clientId,
      clientName: selectedClient?.name ?? clientId,
      type,
      financialYear,
      periodEnd,
      stage: "acceptance",
      partner,
      manager: manager || undefined,
      team: teamInput ? teamInput.split(",").map(t => t.trim()).filter(Boolean) : [partner],
      riskAreas: [],
      workpapers: [],
      pbcRequests: [],
      findings: [],
      createdAt: new Date().toISOString()
    };

    // Fire the bridge event so ClientDesk updates automatically
    processBridgeEvent({
      type: "AuditEngagementCreated",
      engagementId: engagement.id,
      clientId,
      engagementType: type,
      financialYear,
      period: financialYear,
      partner,
      manager: manager || undefined,
      team: engagement.team,
      coversReturns: type === "statutory-audit" ? ["ROC-AOC-4", "ROC-MGT-7"] : type === "tax-audit-44ab" ? ["TDS-26Q"] : [],
      createdAt: engagement.createdAt,
      createdBy: partner
    }, { clients, complianceItems: [] });

    onCreate(engagement);
    notify(`Engagement created for ${selectedClient?.name ?? clientId}`, "green");
  };

  return (
    <form onSubmit={submit} className="pf-form">
      <section className="pf-section">
        <h4>Client</h4>
        <div className="pf-grid two">
          <div className="pf-field">
            <span className="pf-label">Client</span>
            <select value={clientId} onChange={e => setClientId(e.target.value)} required>
              <option value="">Select client</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="pf-field">
            <span className="pf-label">Engagement type</span>
            <select value={type} onChange={e => setType(e.target.value as EngagementType)}>
              {Object.entries(ENGAGEMENT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        {clientCtx && (
          <div className="hint-card" style={{ marginTop: 10 }}>
            <div className="hint-icon">ℹ</div>
            <div>
              <strong>Client context pre-loaded from ClientDesk</strong>
              <p>PAN: {clientCtx.pan ?? "Not on file"} · GSTIN: {clientCtx.gstin ?? "Not on file"} · Books on platform: {clientCtx.booksOnPlatform ? "Yes — TB can be imported" : "No"}</p>
            </div>
          </div>
        )}
      </section>

      <section className="pf-section">
        <h4>Period</h4>
        <div className="pf-grid two">
          <div className="pf-field">
            <span className="pf-label">Financial year</span>
            <select value={financialYear} onChange={e => setFinancialYear(e.target.value)}>
              {["2023-24", "2024-25", "2025-26", "2026-27"].map(fy => <option key={fy} value={fy}>{fy}</option>)}
            </select>
          </div>
          <div className="pf-field">
            <span className="pf-label">Period end date</span>
            <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} />
          </div>
        </div>
      </section>

      <section className="pf-section">
        <h4>Team</h4>
        <div className="pf-grid two">
          <div className="pf-field">
            <span className="pf-label">Engagement partner</span>
            <input value={partner} onChange={e => setPartner(e.target.value)} placeholder="CA Raghav K." required />
          </div>
          <div className="pf-field">
            <span className="pf-label">Manager</span>
            <input value={manager} onChange={e => setManager(e.target.value)} placeholder="Optional" />
          </div>
        </div>
        <div className="pf-field">
          <span className="pf-label">Team members (comma-separated)</span>
          <input value={teamInput} onChange={e => setTeamInput(e.target.value)} placeholder="vikram, priya, akash" />
        </div>
      </section>

      <div className="modal-actions">
        <button type="button" className="button button-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="button" disabled={!clientId || !partner}>Create engagement</button>
      </div>
    </form>
  );
}

// ---------------------------------------- Engagement dashboard

interface EngagementDashboardProps {
  engagement: AuditEngagement;
  onUpdate: (eng: AuditEngagement) => void;
  onBack: () => void;
  notify: (msg: string, tone?: string) => void;
}

type EngTab = "overview" | "risk" | "workpapers" | "pbc" | "findings" | "caro" | "form3cd" | "completion";

export function EngagementDashboard({ engagement, onUpdate, onBack, notify }: EngagementDashboardProps) {
  const [tab, setTab] = useState<EngTab>("overview");
  const completion = engagementCompletionPercent(engagement);
  const nextStage = remainingStages(engagement)[0];
  const readiness = nextStage ? stageReadiness(engagement, nextStage) : null;

  const advanceStage = () => {
    if (!nextStage || !readiness?.ready) return;
    onUpdate({ ...engagement, stage: nextStage, updatedAt: new Date().toISOString() });
    notify(`Moved to ${STAGE_LABELS[nextStage]}`, "green");
  };

  const TABS: Array<{ key: EngTab; label: string; count?: number }> = [
    { key: "overview", label: "Overview" },
    { key: "risk", label: "Risk areas", count: engagement.riskAreas.length },
    { key: "workpapers", label: "Workpapers", count: engagement.workpapers.length },
    { key: "pbc", label: "PBC requests", count: engagement.pbcRequests.filter(p => p.status === "requested").length },
    { key: "findings", label: "Findings", count: engagement.findings.filter(f => f.status === "open").length },
    ...(engagement.type === "statutory-audit" ? [{ key: "caro" as EngTab, label: "CARO 2020" }] : []),
    ...(engagement.type === "tax-audit-44ab" || engagement.type === "tax-audit-44ad" ? [{ key: "form3cd" as EngTab, label: "Form 3CD" }] : []),
    { key: "completion", label: "Completion" },
  ];

  return (
    <div className="view-root">
      <div className="af-eng-header">
        <button className="text-button" onClick={onBack} type="button">← All engagements</button>
        <div className="af-eng-title-row">
          <div>
            <h2>{engagement.clientName}</h2>
            <p className="ws-sub">{ENGAGEMENT_TYPE_LABELS[engagement.type]} · FY {engagement.financialYear}</p>
          </div>
          <div className="af-eng-header-actions">
            <div className="cd-stat-card cd-stat-blue" style={{ minWidth: 80 }}>
              <strong>{completion}%</strong><span>Complete</span>
            </div>
            {nextStage && (
              <div>
                <button
                  className={`button ${readiness?.ready ? "" : "button-secondary"}`}
                  onClick={advanceStage}
                  disabled={!readiness?.ready}
                  title={readiness?.blockers[0]}
                  type="button"
                >
                  → {STAGE_LABELS[nextStage]}
                </button>
                {readiness && !readiness.ready && (
                  <small className="pf-error" style={{ display: "block", marginTop: 4 }}>{readiness.blockers[0]}</small>
                )}
              </div>
            )}
            {engagement.stage === "signed-off" && <span className="badge badge-green" style={{ fontSize: 12 }}>✓ Signed off</span>}
          </div>
        </div>
        <StageRail stage={engagement.stage} completion={completion} />

        <div className="cd-tabs">
          {TABS.map(t => (
            <button key={t.key} className={`cd-tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)} type="button">
              {t.label}
              {t.count !== undefined && t.count > 0 && <span className="cd-tab-count">{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="cd-tab-body">
        {tab === "overview" && <EngOverview engagement={engagement} notify={notify} onUpdate={onUpdate} />}
        {tab === "risk" && <RiskTab engagement={engagement} onUpdate={onUpdate} notify={notify} />}
        {tab === "workpapers" && <WorkpapersTab engagement={engagement} onUpdate={onUpdate} notify={notify} />}
        {tab === "pbc" && <PbcTab engagement={engagement} onUpdate={onUpdate} notify={notify} />}
        {tab === "findings" && <FindingsTab engagement={engagement} onUpdate={onUpdate} notify={notify} />}
        {tab === "caro" && <CaroTab engagement={engagement} onUpdate={onUpdate} notify={notify} />}
        {tab === "form3cd" && <Form3CDTab engagement={engagement} onUpdate={onUpdate} notify={notify} />}
        {tab === "completion" && <CompletionTab engagement={engagement} onUpdate={onUpdate} notify={notify} />}
      </div>
    </div>
  );
}

// ---- Overview tab

function EngOverview({ engagement, onUpdate, notify }: { engagement: AuditEngagement; onUpdate: (e: AuditEngagement) => void; notify: (m: string, t?: string) => void }) {
  const readiness = engagement.stage !== "archived" && engagement.stage !== "signed-off"
    ? stageReadiness(engagement, remainingStages(engagement)[0] ?? "signed-off")
    : null;

  return (
    <div className="cd-details-grid">
      <div className="card">
        <div className="card-header"><strong className="card-title">Engagement details</strong></div>
        <div className="cd-field-list">
          {[
            ["Partner", engagement.partner], ["Manager", engagement.manager], ["Team", engagement.team.join(", ")],
            ["Period end", engagement.periodEnd], ["Stage", STAGE_LABELS[engagement.stage]],
            ["Opinion", engagement.opinion ? OPINION_LABELS[engagement.opinion] : "Not yet decided"],
            ["UDIN", engagement.udinNumber ?? "Not yet generated"],
            ["Report date", engagement.reportDate ?? "—"]
          ].map(([k, v]) => (
            <div key={k as string} className="cd-field-row"><span>{k}</span><strong>{v ?? "—"}</strong></div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><strong className="card-title">Materiality</strong></div>
        {engagement.materiality ? (
          <div className="cd-field-list">
            {[
              ["Basis", engagement.materiality.basis.replace(/-/g, " ")],
              ["Base amount", `₹${engagement.materiality.baseAmount.toLocaleString("en-IN")}`],
              ["Percentage", `${engagement.materiality.percentageApplied}%`],
              ["Overall materiality", `₹${engagement.materiality.overallMateriality.toLocaleString("en-IN")}`],
              ["Performance materiality", `₹${engagement.materiality.performanceMateriality.toLocaleString("en-IN")}`],
              ["Clearly trivial", `₹${engagement.materiality.clearlyTrivialThreshold.toLocaleString("en-IN")}`],
            ].map(([k, v]) => (
              <div key={k as string} className="cd-field-row"><span>{k}</span><strong>{v}</strong></div>
            ))}
          </div>
        ) : (
          <div className="empty-state" style={{ padding: "16px 0" }}>
            <p className="ws-sub">Materiality not yet set.</p>
            <button className="button button-secondary" onClick={() => {
              // Quick-set placeholder materiality for demo
              onUpdate({ ...engagement, materiality: { basis: "profit-before-tax", baseAmount: 10000000, percentageApplied: 5, overallMateriality: 500000, performanceMateriality: 350000, clearlyTrivialThreshold: 25000, calculatedBy: engagement.partner, calculatedAt: new Date().toISOString() } });
              notify("Materiality set (placeholder — edit to reflect actual figures)", "blue");
            }} type="button">Set materiality</button>
          </div>
        )}
      </div>

      {readiness && !readiness.ready && (
        <div className="card" style={{ borderColor: "#fbbf24", background: "#fffbeb" }}>
          <div className="card-header"><strong className="card-title" style={{ color: "#92400e" }}>Blockers for next stage</strong></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {readiness.blockers.map((b, i) => <div key={i} style={{ fontSize: 11, color: "#7c3c00" }}>⛔ {b}</div>)}
            {readiness.warnings.map((w, i) => <div key={i} style={{ fontSize: 11, color: "#a16207" }}>⚠ {w}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Risk areas tab

function RiskTab({ engagement, onUpdate, notify }: { engagement: AuditEngagement; onUpdate: (e: AuditEngagement) => void; notify: (m: string, t?: string) => void }) {
  const [adding, setAdding] = useState(false);
  const [area, setArea] = useState(""); const [fsItem, setFsItem] = useState("");
  const [inherent, setInherent] = useState<RiskArea["inherentRisk"]>("high");
  const [control, setControl] = useState<RiskArea["controlRisk"]>("medium");
  const [isSig, setIsSig] = useState(false); const [isFraud, setIsFraud] = useState(false);
  const [description, setDescription] = useState(""); const [response, setResponse] = useState("");

  const rmmLevel = inherent === "high" || control === "high" ? "high" : inherent === "significant" || control === "significant" ? "significant" : inherent === "medium" || control === "medium" ? "medium" : "low";

  const addRisk = () => {
    if (!area || !description) return;
    const risk: RiskArea = {
      id: uid("risk"), area, fsItem, assertions: ["completeness", "occurrence"],
      inherentRisk: inherent, controlRisk: control, rmmLevel,
      isSignificantRisk: isSig, isFraudRisk: isFraud,
      riskDescription: description, auditResponse: response,
      plannedProcedures: [], identifiedBy: engagement.partner,
      identifiedAt: new Date().toISOString()
    };
    onUpdate({ ...engagement, riskAreas: [...engagement.riskAreas, risk] });
    notify("Risk area added", "green");
    setAdding(false); setArea(""); setFsItem(""); setDescription(""); setResponse("");
  };

  const rmmColor = { high: "red", significant: "red", medium: "orange", low: "green" };

  return (
    <div>
      <div className="view-toolbar" style={{ marginBottom: 12 }}>
        <button className="button" onClick={() => setAdding(true)} type="button">+ Add risk area</button>
      </div>

      {adding && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h4 style={{ margin: "0 0 12px", fontSize: 13 }}>New risk area</h4>
          <div className="pf-grid two">
            <div className="pf-field"><span className="pf-label">Area / account</span><input value={area} onChange={e => setArea(e.target.value)} placeholder="Revenue recognition" /></div>
            <div className="pf-field"><span className="pf-label">FS line item</span><input value={fsItem} onChange={e => setFsItem(e.target.value)} placeholder="Revenue from operations" /></div>
          </div>
          <div className="pf-grid three" style={{ marginTop: 10 }}>
            <div className="pf-field"><span className="pf-label">Inherent risk</span><select value={inherent} onChange={e => setInherent(e.target.value as any)}><option value="high">High</option><option value="significant">Significant</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
            <div className="pf-field"><span className="pf-label">Control risk</span><select value={control} onChange={e => setControl(e.target.value as any)}><option value="high">High</option><option value="significant">Significant</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
            <div className="pf-field"><span className="pf-label">RMM (auto)</span><div style={{ marginTop: 6 }}><span className={`badge badge-${rmmColor[rmmLevel]}`}>{rmmLevel}</span></div></div>
          </div>
          <div className="pf-field" style={{ marginTop: 10 }}><span className="pf-label">Risk description</span><input value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe the risk of material misstatement" /></div>
          <div className="pf-field" style={{ marginTop: 10 }}><span className="pf-label">Planned audit response</span><input value={response} onChange={e => setResponse(e.target.value)} placeholder="Planned procedures to address this risk" /></div>
          <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
            <label className="pf-checkbox"><input type="checkbox" checked={isSig} onChange={e => setIsSig(e.target.checked)} /> Significant risk (SA 315)</label>
            <label className="pf-checkbox"><input type="checkbox" checked={isFraud} onChange={e => setIsFraud(e.target.checked)} /> Fraud risk (SA 240)</label>
          </div>
          <div className="modal-actions" style={{ marginTop: 12 }}>
            <button type="button" className="button button-secondary" onClick={() => setAdding(false)}>Cancel</button>
            <button type="button" className="button" onClick={addRisk} disabled={!area || !description}>Add risk area</button>
          </div>
        </div>
      )}

      {engagement.riskAreas.length === 0 && !adding ? (
        <div className="empty-state"><div className="empty-icon">⚠</div><strong>No risk areas identified yet.</strong><p>Risk assessment is required before fieldwork can begin (SA 315).</p></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Area</th><th>FS item</th><th>Inherent</th><th>Control</th><th>RMM</th><th>Flags</th><th>Response</th></tr></thead>
            <tbody>
              {engagement.riskAreas.map(r => (
                <tr key={r.id}>
                  <td><strong>{r.area}</strong></td>
                  <td>{r.fsItem}</td>
                  <td><span className={`badge badge-${rmmColor[r.inherentRisk]}`}>{r.inherentRisk}</span></td>
                  <td><span className={`badge badge-${rmmColor[r.controlRisk]}`}>{r.controlRisk}</span></td>
                  <td><span className={`badge badge-${rmmColor[r.rmmLevel]}`}>{r.rmmLevel}</span></td>
                  <td>
                    {r.isSignificantRisk && <span className="badge badge-red" style={{ marginRight: 4 }}>SIG</span>}
                    {r.isFraudRisk && <span className="badge badge-orange">FRAUD</span>}
                  </td>
                  <td><small>{r.auditResponse || "—"}</small></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Workpapers tab

function WorkpapersTab({ engagement, onUpdate, notify }: { engagement: AuditEngagement; onUpdate: (e: AuditEngagement) => void; notify: (m: string, t?: string) => void }) {
  const statusColor: Record<string, string> = {
    "not-started": "slate", "in-progress": "blue", completed: "orange",
    reviewed: "purple", "signed-off": "green", "not-applicable": "slate"
  };

  const toggleStatus = (wp: Workpaper) => {
    const cycle: Workpaper["status"][] = ["not-started", "in-progress", "completed", "reviewed", "signed-off"];
    const next = cycle[(cycle.indexOf(wp.status) + 1) % cycle.length];
    const updated = { ...wp, status: next, preparedBy: wp.preparedBy ?? engagement.partner, preparedAt: wp.preparedAt ?? new Date().toISOString() };
    onUpdate({ ...engagement, workpapers: engagement.workpapers.map(w => w.id === wp.id ? updated : w) });
  };

  const addWorkpaper = () => {
    const wp: Workpaper = {
      id: uid("wp"), engagementId: engagement.id, section: "substantive",
      reference: `WP-${engagement.workpapers.length + 1}`, title: "New workpaper",
      status: "not-started", saReferences: [], procedures: []
    };
    onUpdate({ ...engagement, workpapers: [...engagement.workpapers, wp] });
    notify("Workpaper added", "green");
  };

  const grouped = useMemo(() => {
    const sections: Record<string, Workpaper[]> = {};
    for (const wp of engagement.workpapers) {
      (sections[wp.section] = sections[wp.section] ?? []).push(wp);
    }
    return sections;
  }, [engagement.workpapers]);

  return (
    <div>
      <div className="view-toolbar" style={{ marginBottom: 12 }}>
        <button className="button" onClick={addWorkpaper} type="button">+ Add workpaper</button>
      </div>
      {engagement.workpapers.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">📋</div><strong>No workpapers yet.</strong><p>Add workpapers for each audit area. Review is blocked until all are complete.</p></div>
      ) : (
        Object.entries(grouped).map(([section, wps]) => (
          <div key={section} style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: ".05em", marginBottom: 8 }}>{section.replace(/-/g, " ")}</h4>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Ref</th><th>Title</th><th>SA refs</th><th>Prepared by</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {wps.map(wp => (
                    <tr key={wp.id}>
                      <td><code style={{ fontSize: 10, background: "#f1f5f9", padding: "2px 6px", borderRadius: 4 }}>{wp.reference}</code></td>
                      <td><strong>{wp.title}</strong></td>
                      <td><small>{wp.saReferences.join(", ") || "—"}</small></td>
                      <td>{wp.preparedBy ?? "—"}</td>
                      <td><span className={`badge badge-${statusColor[wp.status]}`}>{wp.status.replace(/-/g, " ")}</span></td>
                      <td><button className="text-button" onClick={() => toggleStatus(wp)} type="button">Advance →</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ---- PBC requests tab

function PbcTab({ engagement, onUpdate, notify }: { engagement: AuditEngagement; onUpdate: (e: AuditEngagement) => void; notify: (m: string, t?: string) => void }) {
  const [adding, setAdding] = useState(false);
  const [desc, setDesc] = useState(""); const [from, setFrom] = useState(""); const [due, setDue] = useState("");

  const addPbc = () => {
    if (!desc || !from || !due) return;
    const pbc: PbcRequest = {
      id: uid("pbc"), engagementId: engagement.id, description: desc,
      status: "requested", requestedFrom: from, requestedAt: new Date().toISOString(), dueDate: due
    };
    onUpdate({ ...engagement, pbcRequests: [...engagement.pbcRequests, pbc] });
    notify("PBC request added", "green");
    setAdding(false); setDesc(""); setFrom(""); setDue("");
  };

  const advance = (pbc: PbcRequest) => {
    const cycle: PbcRequest["status"][] = ["requested", "received", "reviewed", "accepted"];
    const next = cycle[(cycle.indexOf(pbc.status) + 1) % cycle.length];
    onUpdate({ ...engagement, pbcRequests: engagement.pbcRequests.map(p => p.id === pbc.id ? { ...p, status: next, receivedAt: next === "received" ? new Date().toISOString() : p.receivedAt, reviewedAt: next === "reviewed" ? new Date().toISOString() : p.reviewedAt, reviewedBy: next === "reviewed" ? engagement.partner : p.reviewedBy } : p) });
  };

  const statusColor: Record<string, string> = { requested: "orange", received: "blue", reviewed: "purple", accepted: "green", rejected: "red" };
  const overdue = engagement.pbcRequests.filter(p => p.status === "requested" && p.dueDate < today()).length;

  return (
    <div>
      <div className="view-toolbar" style={{ marginBottom: 12 }}>
        {overdue > 0 && <span className="badge badge-red">{overdue} overdue from client</span>}
        <button className="button" onClick={() => setAdding(true)} type="button">+ Add request</button>
      </div>

      {adding && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h4 style={{ margin: "0 0 12px", fontSize: 13 }}>New PBC request</h4>
          <div className="pf-field" style={{ marginBottom: 10 }}><span className="pf-label">Document / information required</span><input value={desc} onChange={e => setDesc(e.target.value)} placeholder="e.g. Bank statement — March 2026" /></div>
          <div className="pf-grid two">
            <div className="pf-field"><span className="pf-label">Request from</span><input value={from} onChange={e => setFrom(e.target.value)} placeholder="Accounts Manager" /></div>
            <div className="pf-field"><span className="pf-label">Due by</span><input type="date" value={due} onChange={e => setDue(e.target.value)} /></div>
          </div>
          <div className="modal-actions" style={{ marginTop: 12 }}>
            <button type="button" className="button button-secondary" onClick={() => setAdding(false)}>Cancel</button>
            <button type="button" className="button" onClick={addPbc} disabled={!desc || !from || !due}>Add request</button>
          </div>
        </div>
      )}

      {engagement.pbcRequests.length === 0 && !adding ? (
        <div className="empty-state"><div className="empty-icon">📤</div><strong>No PBC requests yet.</strong><p>Track documents and information requested from the client during fieldwork.</p></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Description</th><th>Requested from</th><th>Due</th><th>Status</th><th>Received</th><th>Action</th></tr></thead>
            <tbody>
              {engagement.pbcRequests.map(pbc => (
                <tr key={pbc.id}>
                  <td><strong>{pbc.description}</strong></td>
                  <td>{pbc.requestedFrom}</td>
                  <td className={pbc.status === "requested" && pbc.dueDate < today() ? "amount-negative" : ""}>{pbc.dueDate}</td>
                  <td><span className={`badge badge-${statusColor[pbc.status]}`}>{pbc.status}</span></td>
                  <td>{pbc.receivedAt ? pbc.receivedAt.slice(0, 10) : "—"}</td>
                  <td>{pbc.status !== "accepted" && <button className="text-button" onClick={() => advance(pbc)} type="button">Mark {pbc.status === "requested" ? "received" : pbc.status === "received" ? "reviewed" : "accepted"} →</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Findings tab

function FindingsTab({ engagement, onUpdate, notify }: { engagement: AuditEngagement; onUpdate: (e: AuditEngagement) => void; notify: (m: string, t?: string) => void }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState(""); const [desc, setDesc] = useState("");
  const [severity, setSeverity] = useState<AuditFinding["severity"]>("moderate");
  const [category, setCategory] = useState<AuditFinding["category"]>("misstatement");
  const [affectsOpinion, setAffectsOpinion] = useState(false);

  const addFinding = () => {
    if (!title || !desc) return;
    const finding: AuditFinding = {
      id: uid("fnd"), engagementId: engagement.id, title, description: desc,
      severity, category, assertions: [], status: "open",
      affectsOpinion, raisedBy: engagement.partner, raisedAt: new Date().toISOString()
    };
    onUpdate({ ...engagement, findings: [...engagement.findings, finding] });
    notify(`Finding raised: ${title}`, severity === "critical" || severity === "significant" ? "orange" : "blue");

    // Bridge: if critical/significant, trigger ClientDesk alert
    processBridgeEvent({ type: "AuditFindingRaised", findingId: finding.id, engagementId: engagement.id, clientId: engagement.clientId, severity, area: category, description: desc, raisedBy: engagement.partner, raisedAt: finding.raisedAt }, { clients: [], complianceItems: [] });

    setAdding(false); setTitle(""); setDesc("");
  };

  const resolve = (f: AuditFinding) => {
    onUpdate({ ...engagement, findings: engagement.findings.map(fi => fi.id === f.id ? { ...fi, status: "resolved", resolvedAt: new Date().toISOString(), resolvedBy: engagement.partner } : fi) });
    notify("Finding resolved", "green");
  };

  return (
    <div>
      <div className="view-toolbar" style={{ marginBottom: 12 }}>
        {engagement.findings.filter(f => f.status === "open").length > 0 && (
          <span className="badge badge-red">{engagement.findings.filter(f => f.status === "open").length} open</span>
        )}
        <button className="button" onClick={() => setAdding(true)} type="button">+ Raise finding</button>
      </div>

      {adding && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h4 style={{ margin: "0 0 12px", fontSize: 13 }}>New finding</h4>
          <div className="pf-grid two">
            <div className="pf-field"><span className="pf-label">Title</span><input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Revenue cut-off error" /></div>
            <div className="pf-grid two" style={{ gap: 8 }}>
              <div className="pf-field"><span className="pf-label">Severity</span><select value={severity} onChange={e => setSeverity(e.target.value as any)}>{Object.entries(FINDING_SEVERITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
              <div className="pf-field"><span className="pf-label">Category</span><select value={category} onChange={e => setCategory(e.target.value as any)}><option value="misstatement">Misstatement</option><option value="control-deficiency">Control deficiency</option><option value="compliance">Compliance</option><option value="disclosure">Disclosure</option><option value="going-concern">Going concern</option><option value="fraud-indicator">Fraud indicator</option><option value="observation">Observation</option></select></div>
            </div>
          </div>
          <div className="pf-field" style={{ marginTop: 10 }}><span className="pf-label">Description</span><input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Describe the finding and evidence" /></div>
          <label className="pf-checkbox" style={{ marginTop: 10 }}><input type="checkbox" checked={affectsOpinion} onChange={e => setAffectsOpinion(e.target.checked)} /> This finding may affect the audit opinion</label>
          <div className="modal-actions" style={{ marginTop: 12 }}>
            <button type="button" className="button button-secondary" onClick={() => setAdding(false)}>Cancel</button>
            <button type="button" className="button" onClick={addFinding} disabled={!title || !desc}>Raise finding</button>
          </div>
        </div>
      )}

      {engagement.findings.length === 0 && !adding ? (
        <div className="empty-state"><div className="empty-icon">🔍</div><strong>No findings raised.</strong><p>Findings are raised during fieldwork and must be resolved or accepted before completion.</p></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Finding</th><th>Category</th><th>Severity</th><th>Opinion impact</th><th>Status</th><th>Raised</th><th>Action</th></tr></thead>
            <tbody>
              {engagement.findings.map(f => (
                <tr key={f.id}>
                  <td><strong>{f.title}</strong><small>{f.description}</small></td>
                  <td>{f.category.replace(/-/g, " ")}</td>
                  <td><SeverityBadge severity={f.severity} /></td>
                  <td>{f.affectsOpinion ? <span className="badge badge-red">Yes</span> : <span className="badge badge-green">No</span>}</td>
                  <td><span className={`badge badge-${f.status === "open" ? "red" : f.status === "resolved" ? "green" : "orange"}`}>{f.status}</span></td>
                  <td>{f.raisedAt.slice(0, 10)}</td>
                  <td>{f.status === "open" && <button className="text-button" onClick={() => resolve(f)} type="button">Resolve</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- CARO 2020 tab

function CaroTab({ engagement, onUpdate, notify }: { engagement: AuditEngagement; onUpdate: (e: AuditEngagement) => void; notify: (m: string, t?: string) => void }) {
  const initCaro = (): CaroClause[] => CARO_2020_CLAUSES.map(c => ({
    clauseNumber: c.clauseNumber, heading: c.heading, response: "pending" as const
  }));

  const clauses: CaroClause[] = engagement.caro ?? initCaro();
  const done = clauses.filter(c => c.response !== "pending").length;

  const updateClause = (clauseNumber: string, response: CaroClause["response"], remarks?: string) => {
    const updated = clauses.map(c => c.clauseNumber === clauseNumber
      ? { ...c, response, remarks: remarks ?? c.remarks, completedBy: engagement.partner, completedAt: new Date().toISOString() }
      : c);
    onUpdate({ ...engagement, caro: updated });
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <strong style={{ fontSize: 12 }}>CARO 2020 — {done}/{clauses.length} clauses completed</strong>
        <div className="progress-track" style={{ flex: 1, maxWidth: 200 }}>
          <div className="progress-fill progress-blue" style={{ width: `${(done / clauses.length) * 100}%` }} />
        </div>
        {done === clauses.length && <span className="badge badge-green">Complete</span>}
      </div>
      <div className="cd-caro-list">
        {clauses.map(clause => (
          <div key={clause.clauseNumber} className={`cd-caro-row ${clause.response === "pending" ? "" : "cd-caro-done"}`}>
            <div className="cd-caro-clause-num">{clause.clauseNumber}</div>
            <div className="cd-caro-body">
              <strong>{clause.heading}</strong>
              {clause.remarks && <p className="ws-sub">{clause.remarks}</p>}
            </div>
            <div className="cd-caro-actions">
              {(["yes", "no", "not-applicable"] as CaroClause["response"][]).map(r => (
                <button
                  key={r}
                  type="button"
                  className={`cd-caro-btn ${clause.response === r ? "active" : ""}`}
                  onClick={() => updateClause(clause.clauseNumber, r)}
                >
                  {r === "yes" ? "✓ Yes" : r === "no" ? "✗ No" : "N/A"}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Form 3CD tab

function Form3CDTab({ engagement, onUpdate, notify }: { engagement: AuditEngagement; onUpdate: (e: AuditEngagement) => void; notify: (m: string, t?: string) => void }) {
  const initClauses = () => FORM_3CD_KEY_CLAUSES.map(c => ({
    clauseNumber: c.clauseNumber, heading: c.heading, applicable: true, completed: false, hasDisclosure: false
  }));
  const clauses = engagement.form3cd ?? initClauses();
  const done = clauses.filter(c => !c.applicable || c.completed).length;

  const toggle = (clauseNumber: string) => {
    const updated = clauses.map(c => c.clauseNumber === clauseNumber
      ? { ...c, completed: !c.completed, completedBy: engagement.partner, completedAt: new Date().toISOString() }
      : c);
    onUpdate({ ...engagement, form3cd: updated });
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <strong style={{ fontSize: 12 }}>Form 3CD — key clauses — {done}/{clauses.length} done</strong>
        <div className="progress-track" style={{ flex: 1, maxWidth: 200 }}>
          <div className="progress-fill progress-blue" style={{ width: `${(done / clauses.length) * 100}%` }} />
        </div>
      </div>
      <div className="cd-caro-list">
        {clauses.map(c => (
          <div key={c.clauseNumber} className={`cd-caro-row ${c.completed ? "cd-caro-done" : ""}`}>
            <div className="cd-caro-clause-num">{c.clauseNumber}</div>
            <div className="cd-caro-body"><strong>{c.heading}</strong></div>
            <div className="cd-caro-actions">
              <label className="pf-checkbox">
                <input type="checkbox" checked={c.completed} onChange={() => toggle(c.clauseNumber)} />
                Done
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Completion tab

function CompletionTab({ engagement, onUpdate, notify }: { engagement: AuditEngagement; onUpdate: (e: AuditEngagement) => void; notify: (m: string, t?: string) => void }) {
  const [opinion, setOpinion] = useState<AuditEngagement["opinion"]>(engagement.opinion);
  const [udin, setUdin] = useState(engagement.udinNumber ?? "");
  const [reportDate, setReportDate] = useState(engagement.reportDate ?? today());

  const save = () => {
    onUpdate({ ...engagement, opinion, udinNumber: udin || undefined, reportDate: reportDate || undefined });
    notify("Completion details saved", "green");
  };

  const repLetterSigned = Boolean(engagement.representationLetter?.signedAt);
  const gcAssessed = Boolean(engagement.goingConcern);

  return (
    <div className="cd-details-grid">
      <div className="card">
        <div className="card-header"><strong className="card-title">Audit opinion</strong></div>
        <div className="pf-field" style={{ marginBottom: 12 }}>
          <span className="pf-label">Opinion</span>
          <select value={opinion ?? ""} onChange={e => setOpinion(e.target.value as any)}>
            <option value="">— Select opinion —</option>
            {Object.entries(OPINION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="pf-grid two">
          <div className="pf-field">
            <span className="pf-label">Report date</span>
            <input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)} />
          </div>
          <div className="pf-field">
            <span className="pf-label">UDIN</span>
            <input value={udin} onChange={e => setUdin(e.target.value)} placeholder="20262705ABCDE12345" />
          </div>
        </div>
        <button className="button" onClick={save} type="button" style={{ marginTop: 12 }}>Save</button>
      </div>

      <div className="card">
        <div className="card-header"><strong className="card-title">Completion checklist</strong></div>
        <div className="cd-field-list">
          {[
            ["Representation letter signed", repLetterSigned],
            ["Going concern assessed", gcAssessed],
            ["All workpapers signed off", engagement.workpapers.every(w => w.status === "signed-off" || w.status === "not-applicable")],
            ["All findings resolved", engagement.findings.every(f => f.status !== "open")],
            ["Opinion decided", Boolean(opinion)],
            ["UDIN recorded", Boolean(udin)],
          ].map(([label, done]) => (
            <div key={label as string} className="cd-field-row">
              <span>{label}</span>
              <span className={`badge badge-${done ? "green" : "red"}`}>{done ? "✓ Done" : "Pending"}</span>
            </div>
          ))}
        </div>
        {!repLetterSigned && (
          <button className="button button-secondary" style={{ marginTop: 10 }} onClick={() => {
            onUpdate({ ...engagement, representationLetter: { engagementId: engagement.id, date: today(), representations: [], signedAt: new Date().toISOString(), signedBy: "Management", signedInCapacity: "Director" } });
            notify("Representation letter marked as signed", "green");
          }} type="button">Mark rep letter signed</button>
        )}
        {!gcAssessed && (
          <button className="button button-secondary" style={{ marginTop: 8 }} onClick={() => {
            onUpdate({ ...engagement, goingConcern: { engagementId: engagement.id, indicators: [], conclusion: "no-doubt", assessedBy: engagement.partner, assessedAt: new Date().toISOString() } });
            notify("Going concern assessed — no doubt", "green");
          }} type="button">Record going concern (no doubt)</button>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------- Engagement list

interface EngagementListProps {
  engagements: AuditEngagement[];
  clients: Client[];
  onSelect: (eng: AuditEngagement) => void;
  onNew: () => void;
}

export function EngagementList({ engagements, clients, onSelect, onNew }: EngagementListProps) {
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState<EngagementStage | "all">("all");

  const filtered = engagements.filter(e =>
    (stageFilter === "all" || e.stage === stageFilter) &&
    (!query.trim() || e.clientName.toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <div className="view-root">
      <div className="view-toolbar">
        <div className="topbar-search" style={{ flex: 1, maxWidth: 300 }}>
          <span>⌕</span>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by client name..." />
        </div>
        <select className="filter-select" value={stageFilter} onChange={e => setStageFilter(e.target.value as any)}>
          <option value="all">All stages</option>
          {STAGE_ORDER.map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
        </select>
        <button className="button" onClick={onNew} type="button">+ New engagement</button>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📂</div>
          <strong>No engagements yet.</strong>
          <p>Create an engagement to begin the audit process. ClientDesk updates automatically.</p>
          <button className="button" onClick={onNew} type="button">New engagement</button>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Client</th><th>Type</th><th>FY</th><th>Partner</th><th>Stage</th><th>Progress</th><th>Findings</th></tr></thead>
            <tbody>
              {filtered.map(eng => {
                const pct = engagementCompletionPercent(eng);
                const openFindings = eng.findings.filter(f => f.status === "open").length;
                return (
                  <tr key={eng.id} onClick={() => onSelect(eng)} style={{ cursor: "pointer" }}>
                    <td><strong>{eng.clientName}</strong></td>
                    <td><small>{ENGAGEMENT_TYPE_LABELS[eng.type]}</small></td>
                    <td>{eng.financialYear}</td>
                    <td>{eng.partner}</td>
                    <td>
                      <span className={`badge badge-${
                        eng.stage === "signed-off" || eng.stage === "archived" ? "green" :
                        eng.stage === "review" || eng.stage === "completion" ? "orange" :
                        eng.stage === "fieldwork" ? "purple" : "blue"
                      }`}>{STAGE_LABELS[eng.stage]}</span>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div className="progress-track" style={{ width: 80 }}>
                          <div className={`progress-fill progress-${pct === 100 ? "green" : pct > 50 ? "blue" : "orange"}`} style={{ width: `${pct}%` }} />
                        </div>
                        <small>{pct}%</small>
                      </div>
                    </td>
                    <td>{openFindings > 0 ? <span className="badge badge-red">{openFindings}</span> : <span className="badge badge-green">Clear</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------- Top-level AuditFlow view

interface AuditFlowProps {
  clients: Client[];
  navigate: (view: string) => void;
  notify: (msg: string, tone?: string | undefined) => void;
}

function LegacyAuditFlowView({ clients, navigate, notify }: AuditFlowProps) {
  const [engagements, setEngagements] = useState<AuditEngagement[]>([]);
  const [selected, setSelected] = useState<AuditEngagement | null>(null);
  const [creating, setCreating] = useState(false);

  const updateEngagement = (updated: AuditEngagement) => {
    setEngagements(prev => prev.map(e => e.id === updated.id ? updated : e));
    setSelected(updated);
  };

  if (creating) {
    return (
      <div className="view-root">
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <button className="suite-home-trigger" onClick={() => navigate("suite")} type="button" aria-label="Open Ledzerly product suite" title="Back to Ledzerly Product Suite"><span/><span/><span/><span/></button>
          <button className="text-button" onClick={() => navigate("suite")} type="button">← Suite</button>
        </div>
        <div className="view-toolbar">
          <button className="text-button" onClick={() => setCreating(false)} type="button">← Cancel</button>
          <h3 style={{ margin: 0, fontSize: 14 }}>New engagement</h3>
        </div>
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "20px 0" }}>
          <NewEngagementForm
            clients={clients}
            onCreate={(eng) => { setEngagements(prev => [eng, ...prev]); setCreating(false); setSelected(eng); }}
            onCancel={() => setCreating(false)}
            notify={notify}
          />
        </div>
      </div>
    );
  }

  if (selected) {
    return (
      <div className="view-root">
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <button className="suite-home-trigger" onClick={() => navigate("suite")} type="button" aria-label="Open Ledzerly product suite" title="Back to Ledzerly Product Suite"><span/><span/><span/><span/></button>
          <button className="text-button" onClick={() => navigate("suite")} type="button">← Suite</button>
        </div>
        <EngagementDashboard
          engagement={selected}
          onUpdate={updateEngagement}
          onBack={() => setSelected(null)}
          notify={notify}
        />
      </div>
    );
  }

  return (
    <div className="view-root">
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
        <button className="suite-home-trigger" onClick={() => navigate("suite")} type="button" aria-label="Open Ledzerly product suite" title="Back to Ledzerly Product Suite"><span/><span/><span/><span/></button>
        <button className="text-button" onClick={() => navigate("suite")} type="button">← Suite</button>
      </div>
      <div className="af-header">
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <img src="/product-icons/auditflow.png" alt="AuditFlow" style={{width:40,height:40,borderRadius:10,flexShrink:0}} />
          <div>
            <h2 style={{fontFamily:"'Nunito',sans-serif",fontWeight:900,margin:"0 0 2px"}}>
              <span style={{color:"#5b8ef0"}}>Audit</span><span style={{color:"#1a2341"}}>Flow</span>
            </h2>
            <p className="ws-sub" style={{margin:0}}>End-to-end audit management · Indian SAs · CARO 2020 · Form 3CD</p>
          </div>
        </div>
        {engagements.length > 0 && (
          <div className="cd-stat-grid" style={{ gap: 8 }}>
            {[
              { label: "Active", value: engagements.filter(e => !["signed-off", "archived", "dropped"].includes(e.stage)).length, color: "blue" },
              { label: "Signed off", value: engagements.filter(e => e.stage === "signed-off").length, color: "green" },
              { label: "Open findings", value: engagements.reduce((sum, e) => sum + e.findings.filter(f => f.status === "open").length, 0), color: "red" },
            ].map(s => (
              <div key={s.label} className={`cd-stat-card cd-stat-${s.color}`} style={{ minWidth: 80 }}>
                <strong>{s.value}</strong><span>{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <EngagementList
        engagements={engagements}
        clients={clients}
        onSelect={setSelected}
        onNew={() => setCreating(true)}
      />
    </div>
  );
}


// The Indian financial-statement audit workspace is the default entry point.
// Existing tax / other engagement prototypes remain accessible separately.
export function AuditFlowView(props: AuditFlowProps) {
  const [legacy, setLegacy] = useState(false);
  return legacy ? <><button className="button button-secondary" onClick={() => setLegacy(false)}>← India audit workspace</button><div role="note" className="inline-alert alert-orange">Legacy engagement prototypes: clause mappings, persistence and report outputs have not been validated for statutory use.</div><LegacyAuditFlowView {...props} /></>
    : <IndiaAuditWorkspace clients={props.clients} navigate={props.navigate} onLegacy={() => setLegacy(true)} />;
}
